import type { PropsWithChildren } from 'react';
import { useCallback, useMemo } from 'react';

import { UserContext } from '../contexts';
import { useWmgSession } from '../hooks/useWmgSession';
import type { AuthApiRoleResource, HasPermissionFunction } from '../types';

const APPLICATION_RESOURCE_PREFIX = 'app:';

function getEntityKey(entity: AuthApiRoleResource): string {
  return `${entity.type === 'APPLICATION' ? APPLICATION_RESOURCE_PREFIX : ''}${entity.id.replace(APPLICATION_RESOURCE_PREFIX, '')}`;
}

export function UserProvider({ children }: PropsWithChildren) {
  const {
    email,
    name,
    permissions,
    features,
    isInternal,
    profile,
    userId,
    userType,
  } = useWmgSession();

  const hasPermission: HasPermissionFunction = useCallback(
    (permission, entityIdOrEntityOrEntities, optionsOrApplication) => {
      if (typeof optionsOrApplication === 'string') {
        return (
          permissions[
            getEntityKey({ id: optionsOrApplication, type: 'APPLICATION' })
          ]?.includes(permission) ?? false
        );
      }

      const { checkOnAll = false } = optionsOrApplication ?? {};

      if (entityIdOrEntityOrEntities) {
        if (Array.isArray(entityIdOrEntityOrEntities)) {
          if (checkOnAll) {
            return entityIdOrEntityOrEntities.every((resource) =>
              permissions[getEntityKey(resource)]?.includes(permission),
            );
          }

          return entityIdOrEntityOrEntities.some((resource) =>
            permissions[getEntityKey(resource)]?.includes(permission),
          );
        }

        if (typeof entityIdOrEntityOrEntities === 'object') {
          return permissions[
            getEntityKey(entityIdOrEntityOrEntities)
          ]?.includes(permission);
        }

        return (
          permissions[entityIdOrEntityOrEntities]?.includes(permission) ?? false
        );
      }

      if (checkOnAll) {
        return Object.values(permissions).every((items) =>
          items.includes(permission),
        );
      }

      return Object.values(permissions).some((items) =>
        items.includes(permission),
      );
    },
    [permissions],
  );

  const user = useMemo(() => {
    return {
      email,
      name,
      permissions,
      hasPermission,
      features,
      isInternal,
      profile,
      userId,
      userType,
    };
  }, [
    email,
    features,
    hasPermission,
    isInternal,
    name,
    permissions,
    profile,
    userId,
    userType,
  ]);

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}

UserProvider.displayName = 'UserProvider';
